import {
  EMPTY_OBJECT,
  HamburgerMenu,
  filterByCellValueMenuItem,
  getShowJSONContextItem,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  type Column,
  type ColumnDef,
  type Exposure,
  type FilterableProperty,
  type Quote,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { compact, isNil } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { useForceCancelCareOrderChildMenuItem } from '../CareOrders/useForceCancelCareOrderChildMenuItem';
import { useShowRFQMenuItem } from './MenuItems/useShowRFQMenuItem';
import { useShowTakerQuoteBreakdownMenuItem } from './MenuItems/useShowTakerQuoteBreakdownMenuItem';
import { colIDToFilterBuilderKey } from './useQuotesFilter';

export function useQuotesMenu({
  openClause,
  filterableProperties,
}: {
  openClause?: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties?: FilterableProperty<string>[];
} = EMPTY_OBJECT): {
  columns: Column[];
  getContextMenuItems: (params: GetContextMenuItemsParams<Quote>) => (MenuItemDef | string)[];
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();
  const { handleClickJson, jsonModal } = useJsonModal();

  const { getForceCancelCareOrderChildQuoteItem, dialog: forceCancelCareOrderQuoteItem } =
    useForceCancelCareOrderChildMenuItem();
  const getShowRFQMenuItem = useShowRFQMenuItem();
  const getShowTakerQuoteBreakdownMenuItem = useShowTakerQuoteBreakdownMenuItem();

  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<Quote>) => {
    const quote = params?.node?.data;
    if (isNil(quote)) {
      return [];
    }

    let menuItems: MenuItemDef[] = [];

    if (filterableProperties != null && openClause != null) {
      menuItems = menuItems.concat(
        filterByCellValueMenuItem({
          params,
          filterableProperties,
          openClause,
          colIDToFilterBuilderKey,
          mixpanel,
        })
      );
    }

    return compact([
      ...menuItems,
      ...(quote
        ? [
            getShowJSONContextItem({ params, handleClickJson }),
            'separator',
            getShowRFQMenuItem(quote),
            getShowTakerQuoteBreakdownMenuItem(quote),
            getForceCancelCareOrderChildQuoteItem(quote),
          ]
        : []),
      ...getDefaultContextMenuItems(params),
    ]);
  });

  const dialogs = useMemo(
    () => (
      <>
        {jsonModal}
        {forceCancelCareOrderQuoteItem}
      </>
    ),
    [jsonModal, forceCancelCareOrderQuoteItem]
  );

  const columns = useMemo(
    () =>
      compact([
        {
          type: 'hamburgerMenu',
          id: 'rowMenu',
          params: {
            renderItems: params => <HamburgerMenu {...params} onShowJson={handleClickJson} />,
          },
        },
      ] satisfies ColumnDef<Exposure>[]),
    [handleClickJson]
  );

  return {
    columns,
    getContextMenuItems,
    dialogs,
  };
}
